import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import rbacService from "../services/rbacService";

// Dynamic Module Management Thunks
export const fetchModules = createAsyncThunk(
  "rbac/fetchModules",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getModules(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchModuleById = createAsyncThunk(
  "rbac/fetchModuleById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getModuleById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createModule = createAsyncThunk(
  "rbac/createModule",
  async (moduleData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createModule(moduleData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateModule = createAsyncThunk(
  "rbac/updateModule",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateModule(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteModule = createAsyncThunk(
  "rbac/deleteModule",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteModule(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const toggleModuleStatus = createAsyncThunk(
  "rbac/toggleModuleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.toggleModuleStatus(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchModuleStats = createAsyncThunk(
  "rbac/fetchModuleStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getModuleStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Dynamic Screen Management Thunks
export const fetchScreens = createAsyncThunk(
  "rbac/fetchScreens",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getScreens(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchScreenById = createAsyncThunk(
  "rbac/fetchScreenById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getScreenById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createScreen = createAsyncThunk(
  "rbac/createScreen",
  async (screenData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createScreen(screenData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateScreen = createAsyncThunk(
  "rbac/updateScreen",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateScreen(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteScreen = createAsyncThunk(
  "rbac/deleteScreen",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteScreen(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const toggleScreenStatus = createAsyncThunk(
  "rbac/toggleScreenStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.toggleScreenStatus(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchSidebarScreens = createAsyncThunk(
  "rbac/fetchSidebarScreens",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getSidebarScreens();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchScreensByModule = createAsyncThunk(
  "rbac/fetchScreensByModule",
  async (moduleId, { rejectWithValue }) => {
    try {
      const response = await rbacService.getScreensByModule(moduleId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchScreenStats = createAsyncThunk(
  "rbac/fetchScreenStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getScreenStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Dynamic Permission Management Thunks
export const fetchAdminPermissions = createAsyncThunk(
  "rbac/fetchAdminPermissions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getAdminPermissions(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchAdminPermissionById = createAsyncThunk(
  "rbac/fetchAdminPermissionById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getAdminPermissionById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createAdminPermission = createAsyncThunk(
  "rbac/createAdminPermission",
  async (permissionData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createAdminPermission(permissionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateAdminPermission = createAsyncThunk(
  "rbac/updateAdminPermission",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateAdminPermission(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteAdminPermission = createAsyncThunk(
  "rbac/deleteAdminPermission",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteAdminPermission(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const togglePermissionStatus = createAsyncThunk(
  "rbac/togglePermissionStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.togglePermissionStatus(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchPermissionsByModule = createAsyncThunk(
  "rbac/fetchPermissionsByModule",
  async (module, { rejectWithValue }) => {
    try {
      const response = await rbacService.getPermissionsByModule(module);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchSystemPermissions = createAsyncThunk(
  "rbac/fetchSystemPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getSystemPermissions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchPermissionStats = createAsyncThunk(
  "rbac/fetchPermissionStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getPermissionStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Enhanced Role Management Thunks
export const fetchRoleStats = createAsyncThunk(
  "rbac/fetchRoleStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getRoleStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchSystemRoles = createAsyncThunk(
  "rbac/fetchSystemRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getSystemRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const toggleRoleStatus = createAsyncThunk(
  "rbac/toggleRoleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.toggleRoleStatus(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const addPermissionToRoleAdmin = createAsyncThunk(
  "rbac/addPermissionToRoleAdmin",
  async ({ roleId, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.addPermissionToRoleAdmin(roleId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const removePermissionFromRoleAdmin = createAsyncThunk(
  "rbac/removePermissionFromRoleAdmin",
  async ({ roleId, permissionId }, { rejectWithValue }) => {
    try {
      const response = await rbacService.removePermissionFromRoleAdmin(
        roleId,
        permissionId,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// User Role Assignment Thunks
export const fetchUsersWithRoles = createAsyncThunk(
  "rbac/fetchUsersWithRoles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getUsersWithRoles(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchUserRoleById = createAsyncThunk(
  "rbac/fetchUserRoleById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getUserRoleById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const assignRoleToUserAdmin = createAsyncThunk(
  "rbac/assignRoleToUserAdmin",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.assignRoleToUser(userId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const removeRoleFromUserAdmin = createAsyncThunk(
  "rbac/removeRoleFromUserAdmin",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await rbacService.removeRoleFromUser(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchUsersByRole = createAsyncThunk(
  "rbac/fetchUsersByRole",
  async (roleName, { rejectWithValue }) => {
    try {
      const response = await rbacService.getUsersByRole(roleName);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchAvailableRoles = createAsyncThunk(
  "rbac/fetchAvailableRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getAvailableRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchRoleAssignmentStats = createAsyncThunk(
  "rbac/fetchRoleAssignmentStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getRoleAssignmentStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const bulkAssignRoles = createAsyncThunk(
  "rbac/bulkAssignRoles",
  async (data, { rejectWithValue }) => {
    try {
      const response = await rbacService.bulkAssignRoles(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Consultant User Management Thunks
export const fetchConsultantUsers = createAsyncThunk(
  "rbac/fetchConsultantUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getConsultantUsers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchConsultantRoles = createAsyncThunk(
  "rbac/fetchConsultantRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getConsultantRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createConsultantUser = createAsyncThunk(
  "rbac/createConsultantUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createConsultantUser(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteConsultantUser = createAsyncThunk(
  "rbac/deleteConsultantUser",
  async (userId, { rejectWithValue }) => {
    try {
      await rbacService.deleteConsultantUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const toggleConsultantUserStatus = createAsyncThunk(
  "rbac/toggleConsultantUserStatus",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await rbacService.toggleConsultantUserStatus(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// CRM Consultant Management Thunks
export const fetchConsultantPermissions = createAsyncThunk(
  "rbac/fetchConsultantPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rbacService.getConsultantPermissions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchConsultantRolesForCRM = createAsyncThunk(
  "rbac/fetchConsultantRolesForCRM",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getConsultantRolesForCRM(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createConsultantRole = createAsyncThunk(
  "rbac/createConsultantRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createConsultantRole(roleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateConsultantRole = createAsyncThunk(
  "rbac/updateConsultantRole",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateConsultantRole(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteConsultantRole = createAsyncThunk(
  "rbac/deleteConsultantRole",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteConsultantRole(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchConsultantStaff = createAsyncThunk(
  "rbac/fetchConsultantStaff",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getConsultantStaff(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createConsultantStaff = createAsyncThunk(
  "rbac/createConsultantStaff",
  async (staffData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createConsultantStaff(staffData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateConsultantStaff = createAsyncThunk(
  "rbac/updateConsultantStaff",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateConsultantStaff(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteConsultantStaff = createAsyncThunk(
  "rbac/deleteConsultantStaff",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteConsultantStaff(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Original Async thunks
export const fetchPermissions = createAsyncThunk(
  "rbac/fetchPermissions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getPermissions(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchPermissionById = createAsyncThunk(
  "rbac/fetchPermissionById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getPermissionById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createPermission = createAsyncThunk(
  "rbac/createPermission",
  async (permissionData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createPermission(permissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updatePermission = createAsyncThunk(
  "rbac/updatePermission",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updatePermission(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deletePermission = createAsyncThunk(
  "rbac/deletePermission",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deletePermission(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchRoles = createAsyncThunk(
  "rbac/fetchRoles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await rbacService.getRoles(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchRoleById = createAsyncThunk(
  "rbac/fetchRoleById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rbacService.getRoleById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createRole = createAsyncThunk(
  "rbac/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await rbacService.createRole(roleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateRole = createAsyncThunk(
  "rbac/updateRole",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await rbacService.updateRole(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteRole = createAsyncThunk(
  "rbac/deleteRole",
  async (id, { rejectWithValue }) => {
    try {
      await rbacService.deleteRole(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const addPermissionToRole = createAsyncThunk(
  "rbac/addPermissionToRole",
  async (
    { roleId, permissionId, conditions, restrictions },
    { rejectWithValue },
  ) => {
    try {
      const response = await rbacService.addPermissionToRole(roleId, {
        permissionId,
        conditions,
        restrictions,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const removePermissionFromRole = createAsyncThunk(
  "rbac/removePermissionFromRole",
  async ({ roleId, permissionId }, { rejectWithValue }) => {
    try {
      const response = await rbacService.removePermissionFromRole(
        roleId,
        permissionId,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchUserRoles = createAsyncThunk(
  "rbac/fetchUserRoles",
  async ({ userId, organizationId }, { rejectWithValue }) => {
    try {
      const response = await rbacService.getUserRoles(userId, {
        organizationId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchRolePermissionMatrix = createAsyncThunk(
  "rbac/fetchRolePermissionMatrix",
  async (organizationId, { rejectWithValue }) => {
    try {
      const response =
        await rbacService.getRolePermissionMatrix(organizationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchUserPermissions = createAsyncThunk(
  "rbac/fetchUserPermissions",
  async ({ userId, organizationId }, { rejectWithValue }) => {
    try {
      const response = await rbacService.getUserPermissions(userId, {
        organizationId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchStatistics = createAsyncThunk(
  "rbac/fetchStatistics",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const permissions = state.rbac.permissions || [];
      const roles = state.rbac.roles || [];

      const statistics = {
        totalPermissions: permissions.length,
        totalRoles: roles.length,
        totalUsers: 0, // Would need separate API call
        highRiskItems: permissions.filter((p) => p.riskLevel === "high").length,
        activePermissions: permissions.filter((p) => p.isActive).length,
        inactivePermissions: permissions.filter((p) => !p.isActive).length,
        activeRoles: roles.filter((r) => r.isActive).length,
        inactiveRoles: roles.filter((r) => !r.isActive).length,
        systemPermissions: permissions.filter((p) => p.isSystem).length,
        systemRoles: roles.filter((r) => r.isSystem).length,
        permissionsByModule: permissions.reduce((acc, p) => {
          acc[p.module] = (acc[p.module] || 0) + 1;
          return acc;
        }, {}),
        rolesByCategory: roles.reduce((acc, r) => {
          acc[r.category] = (acc[r.category] || 0) + 1;
          return acc;
        }, {}),
        riskDistribution: permissions.reduce((acc, p) => {
          acc[p.riskLevel] = (acc[p.riskLevel] || 0) + 1;
          return acc;
        }, {}),
      };

      return statistics;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Initial state
const initialState = {
  permissions: [],
  roles: [],
  userRoles: [],
  rolePermissionMatrix: [],
  userPermissions: [],
  statistics: null,
  modules: [],
  screens: [],
  adminPermissions: [],
  usersWithRoles: [],
  moduleStats: null,
  screenStats: null,
  permissionStats: null,
  roleStats: null,
  roleAssignmentStats: null,
  consultantUsers: [],
  consultantRoles: [],
  availableRoles: [],
  // CRM Consultant Management
  consultantPermissions: [],
  consultantRolesForCRM: [],
  consultantStaff: [],
  consultantStaffPagination: { current: 1, pageSize: 50, total: 0, pages: 0 },
  loading: false,
  error: null,
  filters: {
    permissions: {
      search: "",
      module: "all",
      category: "all",
      isActive: "all",
    },
    roles: {
      search: "",
      category: "all",
      level: "all",
      isActive: "all",
    },
  },
  pagination: {
    permissions: {
      current: 1,
      pageSize: 50,
      total: 0,
      pages: 0,
    },
    roles: {
      current: 1,
      pageSize: 50,
      total: 0,
      pages: 0,
    },
  },
};

// Slice
const rbacSlice = createSlice({
  name: "rbac",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPermission: (state, action) => {
      state.currentPermission = action.payload;
    },
    setCurrentRole: (state, action) => {
      state.currentRole = action.payload;
    },
    setFilters: (state, action) => {
      const { type, filters } = action.payload;
      state.filters[type] = { ...state.filters[type], ...filters };
    },
    resetFilters: (state, action) => {
      const type = action.payload;
      state.filters[type] = initialState.filters[type];
    },
    clearCurrentItems: (state) => {
      state.currentPermission = null;
      state.currentRole = null;
      state.userRoles = [];
      state.userPermissions = null;
    },
    updateLocalPermission: (state, action) => {
      const { id, data } = action.payload;
      const index = state.permissions.findIndex((p) => p._id === id);
      if (index !== -1) {
        state.permissions[index] = { ...state.permissions[index], ...data };
      }
    },
    updateLocalRole: (state, action) => {
      const { id, data } = action.payload;
      const index = state.roles.findIndex((r) => r._id === id);
      if (index !== -1) {
        state.roles[index] = { ...state.roles[index], ...data };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload.permissions;
        state.pagination.permissions = action.payload.pagination;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Permission By Id
      .addCase(fetchPermissionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPermission = action.payload;
      })
      .addCase(fetchPermissionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Permission
      .addCase(createPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions.unshift(action.payload);
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Permission
      .addCase(updatePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.permissions.findIndex(
          (p) => p._id === action.payload._id,
        );
        if (index !== -1) {
          state.permissions[index] = action.payload;
        }
        if (
          state.currentPermission &&
          state.currentPermission._id === action.payload._id
        ) {
          state.currentPermission = action.payload;
        }
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Permission
      .addCase(deletePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = state.permissions.filter(
          (p) => p._id !== action.payload,
        );
        if (
          state.currentPermission &&
          state.currentPermission._id === action.payload
        ) {
          state.currentPermission = null;
        }
      })
      .addCase(deletePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Roles
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.roles;
        state.pagination.roles = action.payload.pagination;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Role By Id
      .addCase(fetchRoleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRole = action.payload;
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Role
      .addCase(createRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles.unshift(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Role
      .addCase(updateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.roles.findIndex(
          (r) => r._id === action.payload._id,
        );
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.currentRole && state.currentRole._id === action.payload._id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Role
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = state.roles.filter((r) => r._id !== action.payload);
        if (state.currentRole && state.currentRole._id === action.payload) {
          state.currentRole = null;
        }
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Permission to Role
      .addCase(addPermissionToRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPermissionToRole.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.roles.findIndex(
          (r) => r._id === action.payload._id,
        );
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.currentRole && state.currentRole._id === action.payload._id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(addPermissionToRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove Permission from Role
      .addCase(removePermissionFromRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePermissionFromRole.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.roles.findIndex(
          (r) => r._id === action.payload._id,
        );
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.currentRole && state.currentRole._id === action.payload._id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(removePermissionFromRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User Roles
      .addCase(fetchUserRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles = action.payload;
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Assign Role to User
      .addCase(assignRoleToUserAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignRoleToUserAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles.push(action.payload);
      })
      .addCase(assignRoleToUserAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove Role from User
      .addCase(removeRoleFromUserAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRoleFromUserAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.userRoles = state.userRoles.filter(
          (ur) =>
            !(
              ur.userId === action.payload.userId &&
              ur.roleId === action.payload.roleId &&
              ur.organizationId === action.payload.organizationId
            ),
        );
      })
      .addCase(removeRoleFromUserAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Role Permission Matrix
      .addCase(fetchRolePermissionMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissionMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.rolePermissionMatrix = action.payload;
      })
      .addCase(fetchRolePermissionMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User Permissions
      .addCase(fetchUserPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.userPermissions = action.payload;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Statistics
      .addCase(fetchStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Dynamic Module Management
      .addCase(fetchModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = action.payload.data;
        state.pagination.modules = action.payload.pagination;
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createModule.fulfilled, (state, action) => {
        state.modules.push(action.payload.data);
      })
      .addCase(updateModule.fulfilled, (state, action) => {
        const index = state.modules.findIndex(
          (m) => m._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.modules[index] = action.payload.data;
        }
      })
      .addCase(deleteModule.fulfilled, (state, action) => {
        state.modules = state.modules.filter((m) => m._id !== action.payload);
      })
      .addCase(toggleModuleStatus.fulfilled, (state, action) => {
        const index = state.modules.findIndex(
          (m) => m._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.modules[index] = action.payload.data;
        }
      })
      .addCase(fetchModuleStats.fulfilled, (state, action) => {
        state.moduleStats = action.payload.data;
      })

      // Dynamic Screen Management
      .addCase(fetchScreens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScreens.fulfilled, (state, action) => {
        state.loading = false;
        state.screens = action.payload.data;
        state.pagination.screens = action.payload.pagination;
      })
      .addCase(fetchScreens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createScreen.fulfilled, (state, action) => {
        state.screens.push(action.payload.data);
      })
      .addCase(updateScreen.fulfilled, (state, action) => {
        const index = state.screens.findIndex(
          (s) => s._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.screens[index] = action.payload.data;
        }
      })
      .addCase(deleteScreen.fulfilled, (state, action) => {
        state.screens = state.screens.filter((s) => s._id !== action.payload);
      })
      .addCase(toggleScreenStatus.fulfilled, (state, action) => {
        const index = state.screens.findIndex(
          (s) => s._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.screens[index] = action.payload.data;
        }
      })
      .addCase(fetchScreenStats.fulfilled, (state, action) => {
        state.screenStats = action.payload.data;
      })

      // Dynamic Permission Management
      .addCase(fetchAdminPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminPermissions.fulfilled, (state, action) => {
        state.loading = false;
        // Backend may return: { success: true, data: [permissions], pagination: {...} }
        // or the nested: { success: true, data: { permissions: [...], pagination: {...} } }
        const payloadData = action.payload.data;
        let permissions = Array.isArray(payloadData)
          ? payloadData
          : payloadData && Array.isArray(payloadData.permissions)
            ? payloadData.permissions
            : [];
        const pager = Array.isArray(payloadData)
          ? action.payload.pagination
          : payloadData && payloadData.pagination;
        // Latest created permissions first
        permissions = [...permissions].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        );
        state.adminPermissions = permissions;
        state.pagination.permissions = pager || {};
      })
      .addCase(fetchAdminPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAdminPermission.fulfilled, (state, action) => {
        state.adminPermissions.push(action.payload.data);
      })
      .addCase(updateAdminPermission.fulfilled, (state, action) => {
        const index = state.adminPermissions.findIndex(
          (p) => p._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.adminPermissions[index] = action.payload.data;
        }
      })
      .addCase(deleteAdminPermission.fulfilled, (state, action) => {
        state.adminPermissions = state.adminPermissions.filter(
          (p) => p._id !== action.payload,
        );
      })
      .addCase(togglePermissionStatus.fulfilled, (state, action) => {
        const index = state.adminPermissions.findIndex(
          (p) => p._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.adminPermissions[index] = action.payload.data;
        }
      })
      .addCase(fetchPermissionStats.fulfilled, (state, action) => {
        state.permissionStats = action.payload.data;
      })

      // Enhanced Role Management
      .addCase(fetchRoleStats.fulfilled, (state, action) => {
        state.roleStats = action.payload.data;
      })

      // User Role Assignment
      .addCase(fetchUsersWithRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsersWithRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.usersWithRoles = action.payload.data;
        state.pagination.users = action.payload.pagination;
      })
      .addCase(fetchUsersWithRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRoleAssignmentStats.fulfilled, (state, action) => {
        state.roleAssignmentStats = action.payload.data;
      })

      // Fetch Available Roles
      .addCase(fetchAvailableRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.availableRoles = action.payload.roles || action.payload;
      })
      .addCase(fetchAvailableRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Consultant Users
      .addCase(fetchConsultantUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultantUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantUsers = action.payload.users || action.payload;
      })
      .addCase(fetchConsultantUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Consultant Roles
      .addCase(fetchConsultantRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultantRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantRoles = action.payload.roles || action.payload;
      })
      .addCase(fetchConsultantRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Consultant User
      .addCase(createConsultantUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConsultantUser.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantUsers) {
          state.consultantUsers.unshift(action.payload);
        }
      })
      .addCase(createConsultantUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Consultant User
      .addCase(deleteConsultantUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConsultantUser.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantUsers) {
          state.consultantUsers = state.consultantUsers.filter(
            (user) => user._id !== action.payload
          );
        }
      })
      .addCase(deleteConsultantUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle Consultant User Status
      .addCase(toggleConsultantUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleConsultantUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantUsers) {
          const index = state.consultantUsers.findIndex(
            (user) => user._id === action.payload._id
          );
          if (index !== -1) {
            state.consultantUsers[index] = action.payload;
          }
        }
      })
      .addCase(toggleConsultantUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CRM Consultant Management
      
      // Fetch Consultant Permissions
      .addCase(fetchConsultantPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultantPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantPermissions = action.payload.availablePermissions || action.payload;
      })
      .addCase(fetchConsultantPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Consultant Roles for CRM
      .addCase(fetchConsultantRolesForCRM.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultantRolesForCRM.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantRolesForCRM = action.payload.roles || action.payload;
      })
      .addCase(fetchConsultantRolesForCRM.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Consultant Role
      .addCase(createConsultantRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConsultantRole.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantRolesForCRM) {
          state.consultantRolesForCRM.unshift(action.payload);
        }
      })
      .addCase(createConsultantRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Consultant Role
      .addCase(updateConsultantRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConsultantRole.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantRolesForCRM) {
          const index = state.consultantRolesForCRM.findIndex(
            (role) => role._id === action.payload._id
          );
          if (index !== -1) {
            state.consultantRolesForCRM[index] = action.payload;
          }
        }
      })
      .addCase(updateConsultantRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Consultant Role
      .addCase(deleteConsultantRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConsultantRole.fulfilled, (state, action) => {
        state.loading = false;
        if (state.consultantRolesForCRM) {
          state.consultantRolesForCRM = state.consultantRolesForCRM.filter(
            (role) => role._id !== action.payload
          );
        }
      })
      .addCase(deleteConsultantRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Consultant Staff
      .addCase(fetchConsultantStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultantStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantStaff = action.payload.data?.users || action.payload.users || action.payload;
        const pg = action.payload.data?.pagination || action.payload.pagination;
        if (pg) state.consultantStaffPagination = pg;
      })
      .addCase(fetchConsultantStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Consultant Staff
      .addCase(createConsultantStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConsultantStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantStaff.unshift(action.payload);
      })
      .addCase(createConsultantStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Consultant Staff
      .addCase(updateConsultantStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConsultantStaff.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.consultantStaff.findIndex(
          (staff) => staff._id === action.payload._id
        );
        if (index !== -1) {
          state.consultantStaff[index] = action.payload;
        }
      })
      .addCase(updateConsultantStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Consultant Staff
      .addCase(deleteConsultantStaff.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConsultantStaff.fulfilled, (state, action) => {
        state.loading = false;
        state.consultantStaff = state.consultantStaff.filter(
          (staff) => staff._id !== action.payload
        );
      })
      .addCase(deleteConsultantStaff.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export all actions (both sync and async)
export const rbacActions = {
  ...rbacSlice.actions,
  // Async thunks
  fetchPermissions,
  fetchPermissionById,
  createPermission,
  updatePermission,
  deletePermission,
  fetchRoles,
  fetchRoleById,
  createRole,
  updateRole,
  deleteRole,
  addPermissionToRole,
  removePermissionFromRole,
  fetchUserRoles,
  assignRoleToUserAdmin,
  removeRoleFromUserAdmin,
  fetchRolePermissionMatrix,
  fetchUserPermissions,
  fetchStatistics,
  // Dynamic RBAC thunks
  fetchModules,
  fetchModuleById,
  createModule,
  updateModule,
  deleteModule,
  toggleModuleStatus,
  fetchModuleStats,
  fetchScreens,
  fetchScreenById,
  createScreen,
  updateScreen,
  deleteScreen,
  toggleScreenStatus,
  fetchSidebarScreens,
  fetchScreensByModule,
  fetchScreenStats,
  fetchAdminPermissions,
  fetchAdminPermissionById,
  createAdminPermission,
  updateAdminPermission,
  deleteAdminPermission,
  togglePermissionStatus,
  fetchPermissionsByModule,
  fetchSystemPermissions,
  fetchPermissionStats,
  fetchRoleStats,
  fetchSystemRoles,
  toggleRoleStatus,
  addPermissionToRoleAdmin,
  removePermissionFromRoleAdmin,
  fetchUsersWithRoles,
  fetchUserRoleById,
  fetchUsersByRole,
  fetchAvailableRoles,
  fetchRoleAssignmentStats,
  bulkAssignRoles,
  fetchConsultantUsers,
  fetchConsultantRoles,
  createConsultantUser,
  deleteConsultantUser,
  toggleConsultantUserStatus,
  fetchConsultantPermissions,
  fetchConsultantRolesForCRM,
  createConsultantRole,
  updateConsultantRole,
  deleteConsultantRole,
  fetchConsultantStaff,
  createConsultantStaff,
  updateConsultantStaff,
  deleteConsultantStaff,
};

export default rbacSlice.reducer;
